// Port of ios/Sources/ViewModels/VoiceNoteControls.swift (Kotlin twin: VoiceNoteControls.kt).
//
// The two pieces of voice-note arithmetic that a paused recording and a skip button introduce.

/**
 * How long a take has been RECORDING, which is not the same as how long ago it started.
 *
 * "Allow me to pause, auto pause it if I receive a call or if I have to switch up" (user,
 * 2026-09-12). The moment a recording can be paused, the clock on screen can no longer be
 * `now - startedAt`: that counts the interruption too, and the minimum-length check on stop reads
 * the same number, so a take paused for a minute and holding nothing would pass it. Paused spans
 * are accumulated and subtracted. All values in seconds.
 */
export const VoiceRecordingClock = {
  /**
   * @param startedAt when recording started.
   * @param accumulatedPaused total of every span already completed between a pause and a resume.
   * @param pausedAt when the CURRENT pause began, or null while recording.
   */
  recorded(startedAt: number, accumulatedPaused: number, pausedAt: number | null, now: number): number {
    const wall = now - startedAt;
    const pausedSoFar = accumulatedPaused + (pausedAt == null ? 0 : Math.max(0, now - pausedAt));
    // Never negative: a clock that steps backwards must read 0:00, not a negative duration.
    return Math.max(0, wall - pausedSoFar);
  },

  /** The paused total after a resume — the span that just ended, added on. */
  accumulatedAfterResume(accumulatedPaused: number, pausedAt: number, now: number): number {
    return accumulatedPaused + Math.max(0, now - pausedAt);
  },
} as const;

/**
 * Where a skip-back / skip-forward button lands.
 *
 * "Allow me to replay it forward and backward" (user, 2026-09-12). Dragging the bar is the wrong
 * tool for "say that again": a voice note's bar is a couple of hundred points wide, so one
 * finger-width is several seconds. A button that means exactly five seconds can.
 */
export const VoiceNoteSeek = {
  /** Five seconds: long enough to re-hear a missed word, short enough to press twice. */
  step: 5,

  /** `duration` 0 = not known yet: nothing to seek within, the position must not move. */
  target(position: number, delta: number, duration: number): number {
    if (!(duration > 0)) return position;
    return Math.min(Math.max(0, position + delta), duration);
  },

  canStepBack(position: number, duration: number): boolean {
    return duration > 0 && position > 0;
  },

  canStepForward(position: number, duration: number): boolean {
    return duration > 0 && position < duration;
  },
} as const;
